import data from "../../shared/sharedData";

export default {
  name: "Housing Information",
  prefix: "D",
  sections: [
    {
      name: "",
      fields: [
        {
          code: "D.1",
          machine_name: "eta_790a_d1_housing_address",
          widget: "text_area",
          label: "Housing Address/Location",
          validate: [
            "required",
            {
              fieldComparison: "eta_790a_d1_housing_address",
              message:
                "Housing Address/Location must contain at least 5 characters",
              needsFieldValues: true,
              extraData: {
                operator: "minLength",
                valueOf: 5,
              },
            },
          ],
          width: 8,
          rows: 5,
          inputLimit: 120,
          tooltip:
            "Enter the street address of the location where the housing for workers is located. Use commonly understood street or highway numbers and names. For applications involving agricultural labor or services on work itineraries where the use of mobile housing is permitted, enter the nearest geographic location of the mobile housing unit where it resides at the time of filing the Form ETA-790A.",
        },
        {
          code: "D.2",
          machine_name: "eta_790a_d2_housing_city",
          widget: "text",
          label: "City",
          validate: [
            "required",
            {
              fieldComparison: "eta_790a_d2_housing_city",
              message: "City must contain at least 2 characters",
              needsFieldValues: true,
              extraData: {
                operator: "minLength",
                valueOf: 2,
              },
            },
          ],
          maxLength: 35,
          tooltip: "Enter the city in which the housing is located.",
        },
        {
          code: "D.3",
          machine_name: "eta_790a_d3_housing_state_id",
          widget: "select",
          label: "State",
          selection: data.state,
          state: true,
          validate: ["required"],
          tooltip:
            "Enter the State/District/Territory in which the housing is located.",
        },
        {
          code: "D.4",
          machine_name: "eta_790a_d4_housing_zip",
          widget: "text",
          label: "Postal Code",
          validate: ["required", "postal"],
          maxLength: 10,
          tooltip:
            "Enter the postal (zip) code in which the housing is located.",
        },
        {
          code: "D.5",
          machine_name: "eta_790a_d5_housing_county_id",
          widget: "countyselect",
          label: "County",
          required: true,
          validate: [
            "required",
            {
              required_county: "eta_790a_d3_housing_state_id",
              message: "",
              needsFieldValues: true,
            },
          ],
          maxLength: 60,
          disabled: true,
          disableWhenField: "eta_790a_d3_housing_state_id",
          tooltip: "Enter the county in which the housing is located.",
        },
      ],
    },
    {
      name: "",
      coloring: "none",
      fields: [
        {
          isGroup: {
            code: "D.6",
            machine_name: "eta_790a_d6_housing_type",
            label: "Type of Housing",
            required: true,
            coloring: "none",
            fields: [
              {
                widget: "radio",
                machine_name: "eta_790a_d6_housing_type",
                choice: "Employer-provided (including mobile or range)",
                // label: "Employer-provided (including mobile or range)",
                value: "Employer",
                // hidden: true,
                validate: ["required"],
              },
              {
                widget: "radio",
                machine_name: "eta_790a_d6_housing_type",
                choice: "Rental or public accommodations",
                // label: "Rental or public accommodations",
                value: "Rental",
                // hidden: true,
                validate: ["required"],
              },
            ],
            tooltip:
              "Identify the type of housing that will be provided to workers at this location. Examples include camp, cabin, barracks,or two-story house (private, rental, public accommodation).",
          },
        },
        {
          code: "D.7",
          machine_name: "eta_790a_d7_housing_total_units",
          widget: "text",
          label: "Total Units",
          validate: ["required"],
          parse: "positiveWholeNumber",
          maxLength: 4,
          tooltip:
            "Enter the total number of housing units available to house workers at this location.",
        },
        {
          code: "D.8",
          machine_name: "eta_790a_d8_housing_total_occ",
          widget: "text",
          label: "Total Occupancy",
          validate: ["required"],
          parse: "positiveWholeNumber",
          maxLength: 4,
          tooltip:
            "Enter the total occupancy capacity for all of the housing units identified in item 7 above.",
        },
        {
          isGroup: {
            code: "D.9",
            label:
              "Identify the entity that determined the housing met all applicable standards:",
            tooltip: `Select from the following options to identify each entity that has determined, or will determine before certification, that the
              housing identified in Items D.1-8 meets all applicable standards, as required. Check all boxes applicable. <b>Reminder:</b>
              The CO cannot issue certification unless and until the employer submits evidence in connection with the Form ETA-9142A
              that all housing provided or secured for workers meets applicable standards.
              <br>
              <br>
              <ul>
                <li>
                  <i>Local Authority</i>: Select this option if “rental or public accommodations” is selected in Item D.6 and a local authority has
                  determined or will determine compliance. <b>Note:</b> The employer must submit a copy of the inspection report,
                  certification, or other official documentation from the local authority to the CO. This documentation may include, but is
                  not limited to, a certificate from the local Department of Health or building department (e.g., the current certificate of
                  occupancy). See 20 CFR 655.122(d)(1)(ii) and (d)(6)(iii).
                </li>
                <li>
                  <i>SWA</i>: Select this option if either “employer-provided” or “rental or public accommodations” is selected in Item D.6 and
                  the SWA has determined or will determine compliance. Select this option where the SWA has inspected or will inspect
                  the housing itself or through a local, State, or Federal authority that the SWA engages. <b>Note</b>: The employer must
                  submit a copy of the SWA inspection report or certification to the CO. See 20 CFR 655.122(d)(1)(i) and (d)(6)(ii); see
                  also 20 CFR 655.230 and .304. If the employer is an H-2A Labor Contractor and will use housing owned, operated, or
                  secured by one or more fixed-site agricultural businesses, the employer must secure a written statement from each of
                  the fixed-site agricultural businesses stating that “[a]ll housing used by workers and owned, operated, or secured by [it]
                  complies with the applicable housing standards in 20 CFR 655.122(d) and, if applicable, 655.235 or 655.304” and
                  attach to this statement a copy of the SWA certification. See Form ETA-9142A, Appendix A, Section B.16(v).
                </li>
                <li>
                  <i>Other State Authority</i>: Select this option if “rental or public accommodations” is selected in Item D.6 and a State
                  authority other than the SWA has determined or will determine compliance. <b>Note</b>: The employer must submit a copy
                  of the inspection report, certification, or other official documentation from the state authority to the CO. This
                  documentation may include, but is not limited to, a certificate from the State Department of Health (e.g., the current
                  certificate of occupancy). See 20 CFR 655.122(d)(1)(ii) and (d)(6)(iii).
                </li>
                <li>
                  <i>Federal Authority</i>: Select this option if “rental or public accommodations” is selected in Item D.6 and a Federal
                  authority has determined or will determine compliance. <b>Note</b>: The employer must submit to the CO a copy of the
                  inspection report, certification, or other official documentation from the Federal authority. This documentation may
                  include, but is not limited to, a certificate from the Occupational Safety and Health Administration (OSHA). See 20
                  CFR 655.122(d)(1)(ii) and (d)(6)(iii).
                </li>
                <li>
                  <i>Other</i>: Select this option and enter “Employer” if “rental or public accommodations” is selected in Item D.6 and no
                  inspection is required, or if local and/or State inspection is required, but the local and State standards do not address 
                  all housing criteria (e.g., square footage per occupant). <b>Note</b>: The employer must submit a written statement to the CO. See 20 CFR 655.122(d)(6)(iii).
                  <br>
                  <br>
                  Select this option and enter “Employer” if “Employer-provided” is selected in Item D.6 and the employer will submit a
                  still-valid SWA housing certification. Also select the SWA box. <b>Note</b>: The employer must submit a written selfcertification statement to the CO. See 20 CFR 655.230(c) and 655.235. 
                  <br>
                  <br>
                  Select this option and enter “Canadian” if “Employer-provided” is selected in Item D.6 and the employer’s unit is
                  subject to inspection under 20 CFR 655.304(a)(2).
                </li>
              </ul>
              `,
            coloring: "none",
            disabled: true,
            columns: false,
            required: true,
            machine_name: "eta_790a_d9_housing_standards",
            // validate: ["required"],
            fields: [
              {
                widget: "checkbox_old",
                machine_name: "eta_790a_d9_housing_standards_local",
                choice: "Local Authority",
                label: "Local",
                hidden: true,
                value: "YES",
                // validate: ['required'],
                validate: [
                  // "required",
                  {
                    subsetOfSetRequired: "eta_790a_d9_housing_standards",
                    message: "This field is required",
                    needsFieldValues: true,
                    extraData: {
                      numRequired: 1,
                      fieldArray: [
                        "eta_790a_d9_housing_standards_local",
                        "eta_790a_d9_housing_standards_swa",
                        "eta_790a_d9_housing_standards_other_state",
                        "eta_790a_d9_housing_standards_other",
                        "eta_790a_d9_housing_standards_federal",
                      ],
                    },
                  },
                ],
              },
              {
                widget: "checkbox_old",
                machine_name: "eta_790a_d9_housing_standards_swa",
                choice: "SWA",
                label: "SWA",
                value: "YES",
                tooltip: "hi",
                hidden: true,
              },
              {
                widget: "checkbox_old",
                machine_name: "eta_790a_d9_housing_standards_other_state",
                choice: "Other State Authority",
                label: "Other State",
                value: "YES",
                hidden: true,
              },
              {
                widget: "checkbox_old",
                machine_name: "eta_790a_d9_housing_standards_federal",
                choice: "Federal Authority",
                label: "Federal",
                value: "YES",
                hidden: true,
              },
              {
                widget: "checkbox_old",
                machine_name: "eta_790a_d9_housing_standards_other",
                choice: "Other (Specify)",
                label: "Other (Specify)",
                value: "YES",
                hidden: true,
                clearSchema: ["eta_790a_d9_housing_standards_other_text"],
              },
            ],
          },
        },
        {
          label: "Specify: ",
          machine_name: "eta_790a_d9_housing_standards_other_text", // need machine name here for group level error messaging
          widget: "text_area",
          width: 4,
          rows: 2,
          inputLimit: 25,
          validate: ["required"],
          isVisible: {
            when: "eta_790a_d9_housing_standards_other",
            is: "YES",
          },
          tooltip: `Because of the selection of “Other” in question D.9, enter the specific U.S. enter the specific entity that determined the housing met all applicable standards.”`,
        },
        {
          code: "D.10",
          machine_name: "eta_790a_d10_housing_additional_info",
          widget: "text_area",
          label:
            'Additional Housing Information (If no additional information, enter "NONE" below)',
          validate: ["required"],
          width: 8,
          rows: 10,
          inputLimit: 500,
          tooltip:
            'Enter any additional information about the housing.  Examples may include more specific directions on how workers can reach the housing and/or GPS coordinates, especially in very rural and isolated geographic areas; availability of family units and/or single rooms; utilities (e.g., gas, electricity, and heat); and/or arrangements for utility hookups.  For mobile units, explain where the mobile units will be used (e.g., "mobile unit will travel with the workers to various range locations through Jefferson, Fremont, and Bonneville Counties (Idaho) and Teton and Lincoln Counties (Wyoming)").  If no additional information concerning the housing is needed, enter "<strong>NONE</strong>" in the space provided.',
        },
      ],
    },
    {
      name: "Additional Housing",
      coloring: "none",
      fields: [
        {
          isGroup: {
            code: "D.11",
            label:
              "Will housing be offered at additional locations other than the one identified above?",
            machine_name: "eta_790a_d11_is_housing_addendum_b_completed", //not sure if this is the correct machine name
            tooltip: `In circumstances where workers will be provided housing at additional locations and/or additional space is needed to
              identify all available housing units for workers at the address listed in Items 1 through 5 above, select “Yes” and submit a
              completed Form ETA-790A, Addendum B, providing additional information on housing that will be provided to workers. If
              no additional information concerning the housing is needed, select “<b>N/A</b>.”`,
            coloring: "none",
            required: true,
            disabled: true,
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "YES",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "N/A",
                value: "N/A",
                validate: ["required"],
                clearSchema: ["eta_790a_addendum_b_housing"],
              },
            ],
          },
        },
      ],
    },
  ],
};
